import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowRight } from "lucide-react";
import confetti from "canvas-confetti";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useHomeRoute } from "@/hooks/use-home-route";
import { useMyDataQuery } from "@/generated/graphql";
import { Link } from "react-router-dom";
import { trackPurchaseCompleted } from "@/lib/analytics/trackPurchaseCompleted";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const PurchaseComplete = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { route: homeRoute } = useHomeRoute();
  const { data: myData } = useMyDataQuery();
  const isOtherChannels = myData?.myData?.onboarding_objective === 'other_channels';
  const subscription = myData?.myData?.subscription;

  useEffect(() => {
    trackPurchaseCompleted(subscription);
  }, [subscription]);

  useEffect(() => {
    // Launch confetti when the page loads
    const launchConfetti = () => {
      // Launch confetti from multiple points across the top of the page
      const launchFromPosition = (xOrigin: number) => {
        const defaults = {
          origin: { x: xOrigin, y: 0 }
        };

        function fire(particleRatio: number, opts: any) {
          confetti({
            ...defaults,
            ...opts,
            particleCount: Math.floor(300 * particleRatio)
          });
        }

        fire(0.25, {
          spread: 26,
          startVelocity: 55,
        });

        fire(0.2, {
          spread: 60,
        });

        fire(0.35, {
          spread: 100,
          decay: 0.91,
          scalar: 0.8
        });

        fire(0.1, {
          spread: 120,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2
        });

        fire(0.1, {
          spread: 120,
          startVelocity: 45,
        });
      };

      // Launch confetti from multiple positions across the screen
      launchFromPosition(0.1);   // Left side
      launchFromPosition(0.3);   // Left-center
      launchFromPosition(0.5);   // Center
      launchFromPosition(0.7);   // Right-center
      launchFromPosition(0.9);   // Right side
    };

    // Launch confetti immediately
    launchConfetti();

    // Launch again after a short delay for extra celebration
    const timer = setTimeout(() => {
      launchConfetti();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleGoToProjects = () => {
    navigate(homeRoute);
  };

  return (
    <Layout onStartTour={() => {}}>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl bg-card border-border shadow-glow">
          <CardContent className="text-center p-8 lg:p-12">
            {/* Success Icon */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
                <div className="relative bg-green-500 rounded-full p-6">
                  <CheckCircle className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="mb-8">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {t('purchaseComplete.thankYou')}
              </h1>
              <div className="w-24 h-1 bg-gradient-primary mx-auto rounded-full mb-6"></div>
              <p className="text-xl text-muted-foreground mb-4">
                {t('purchaseComplete.paymentProcessed')}
              </p>
              <p className="text-lg text-muted-foreground max-w-md mx-auto">
                {t('purchaseComplete.nowHaveAccess')}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mb-8 flex flex-col items-center gap-4">
              {isOtherChannels && (
                <Button
                  asChild
                  size="xl"
                  className="w-full sm:w-auto px-10 py-6 text-lg font-bold bg-[#25D366] text-white hover:bg-[#20bd5a] hover:scale-105 transition-all duration-300"
                >
                  <Link to="https://chat.whatsapp.com/BV9LMJnV2gV5DQbpt5ZNoA?s=cl&p=i&mlu=4" target="_blank" rel="noopener noreferrer">
                    <WhatsAppIcon className="w-6 h-6 mr-2" />
                    {t('purchaseComplete.joinCommunity')}
                  </Link>
                </Button>
              )}
              <Button
                onClick={handleGoToProjects}
                variant={isOtherChannels ? "outline" : "gradient"}
                size="lg"
                className="w-full sm:w-auto px-8 py-4 text-lg"
              >
                {t('purchaseComplete.startCreating')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Additional Info */}
            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-2">
                {t('purchaseComplete.readyToCreate')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PurchaseComplete;
